import React, { useState } from "react";
import moment from "moment-timezone";
import ReactModal from "react-modal";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import ai from "../../helpers/axios";
import { LoadingButton } from "../shared/loading-button";
import { LegSide, LegDetails } from "./match-pair-card";

import "./match-pair-card.css";
import getApiErrorMessage from "../../helpers/get-api-error-message";

const keyTypeLabel = (type) => {
  if (type === "lead") return "Lead";
  if (type === "atc") return "ATC";
  return type || "Unknown";
};

const ArchivedMatchCard = ({ row, onUnarchived }) => {
  const [expanded, setExpanded] = useState(false);
  const [unarchiving, setUnarchiving] = useState(false);
  const [removed, setRemoved] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  if (!row || removed) return null;

  const legA = row.legA || null;
  const legB = row.legB || null;

  const headerLabel = `${keyTypeLabel(row.keyTypeA)} \u2194 ${keyTypeLabel(
    row.keyTypeB
  )}`;

  const archivedWhen = row.createdAt ? moment(row.createdAt).fromNow() : "";

  const openConfirm = (e) => {
    e.stopPropagation();
    setShowConfirm(true);
  };

  const closeConfirm = () => {
    if (unarchiving) return;
    setShowConfirm(false);
  };

  const confirmUnarchive = async () => {
    setUnarchiving(true);
    try {
      const token = await getAccessTokenSilently().catch((err) => {
        console.error(err);
        return loginWithRedirect();
      });
      await ai
        .auth(token)
        .delete(`/api/flightLegs/archivedMatches/${row._id}`);

      toast.success("Match unarchived successfully", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      setShowConfirm(false);
      setRemoved(true);
      if (onUnarchived) onUnarchived(row);
    } catch (err) {
      console.error("Unarchive match failed:", err);
      toast.error(
        getApiErrorMessage(err, "Failed to unarchive match. Please try again."),
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );
      setUnarchiving(false);
    }
  };

  return (
    <div className="match-pair-card">
      <div className="match-pair-card__priority-banner match-pair-card__priority-banner--archived">
        Archived Match · {headerLabel}
      </div>

      {(row.archivedByName || archivedWhen || row.notes) && (
        <div className="archived-match-card__meta">
          <div className="archived-match-card__meta-left">
            {row.archivedByName && (
              <span className="archived-match-card__meta-item">
                Archived by <strong>{row.archivedByName}</strong>
              </span>
            )}
            {archivedWhen && (
              <span className="archived-match-card__meta-item">
                {archivedWhen}
              </span>
            )}
          </div>
          {row.notes && (
            <div className="archived-match-card__notes" title={row.notes}>
              “{row.notes}”
            </div>
          )}
        </div>
      )}

      <div
        className="match-pair-card__collapsed"
        onClick={() => setExpanded(!expanded)}
      >
        <LegSide leg={legA} align="left" />

        <div className="match-pair-card__separator">
          <div className="match-pair-card__match-icon">&#8596;</div>
          <div className="match-pair-card__match-type">archived</div>
        </div>

        <LegSide leg={legB} align="right" />
      </div>

      <div
        className="match-pair-card__toggle-row"
        onClick={() => setExpanded(!expanded)}
      >
        <span className="match-pair-card__toggle-text">
          {expanded ? "Show less" : "Show more"}
        </span>
      </div>

      <div
        className={`match-pair-card__expand-wrapper ${
          expanded ? "match-pair-card__expand-wrapper--open" : ""
        }`}
      >
        <div className="match-pair-card__expand-inner">
          <div className="match-pair-card__expanded">
            <div className="match-pair-card__expanded-grid">
              <LegDetails leg={legA} align="left" />
              <LegDetails leg={legB} align="right" />
            </div>
            <div className="match-pair-card__actions">
              <button
                className="match-pair-card__archive-btn"
                onClick={openConfirm}
                disabled={unarchiving}
              >
                Unarchive Match
              </button>
            </div>
          </div>
        </div>
      </div>

      <ReactModal
        isOpen={showConfirm}
        onRequestClose={closeConfirm}
        shouldCloseOnEsc={!unarchiving}
        shouldCloseOnOverlayClick={!unarchiving}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            border: "none",
            maxWidth: "600px",
            maxHeight: "80vh",
            minWidth: "calc(min(500px, 95vw))",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>Unarchive Match</h4>
        </div>

        <div className="modal-body base-modal modal-body-with-scroll">
          <div className="w-100 mt-20">
            Unarchiving this match will allow these two records to match again
            on the next PFP sync. If they still qualify, the match will reappear
            in Flight Matches. Continue?
          </div>
        </div>

        <div className="modal-footer w-100 mt-20 d-flex gap-20">
          <button
            className="base-button evo-blue flex-half"
            type="button"
            onClick={closeConfirm}
            disabled={unarchiving}
          >
            Cancel
          </button>

          <LoadingButton
            className="base-button btn-danger flex-half"
            type="button"
            onClick={confirmUnarchive}
            isLoading={unarchiving}
          >
            Unarchive Match
          </LoadingButton>
        </div>
      </ReactModal>
    </div>
  );
};

export default ArchivedMatchCard;
